/**
 * Format a tournament date+time for display in the user's local timezone.
 * The stored time is assumed to be in Europe/Paris timezone.
 *
 * @param {string} date - ISO date string (YYYY-MM-DD)
 * @param {string|null} time - Time string (HH:mm) in Paris timezone, or null
 * @param {object} options - { style: 'long' | 'short' }
 * @returns {string} Formatted date/time string
 */
export function formatTournamentDateTime(date, time, { style = 'long' } = {}) {
  if (!date) return '';

  if (time) {
    // Build an ISO string anchored to Europe/Paris
    // Create a date string that we parse as Paris time
    const isoString = `${date}T${time}:00`;

    // Use Intl to figure out Paris offset for this date, then build a proper UTC date
    const parisDate = new Date(isoString);

    // Get the Paris timezone offset for this specific datetime
    const parisFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Paris',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });

    // The trick: format the UTC-as-if-local date in Paris tz, then compute offset
    // Instead, directly construct the date in Paris tz using a known approach:
    const utcDate = zonedTimeToUTC(date, time, 'Europe/Paris');

    const dateOptions = style === 'long'
      ? { day: 'numeric', month: 'long', year: 'numeric' }
      : { day: '2-digit', month: '2-digit', year: 'numeric' };

    const formattedDate = utcDate.toLocaleDateString(undefined, dateOptions);
    const formattedTime = utcDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${formattedDate} à ${formattedTime}`;
  }

  // Date only - no timezone conversion needed
  const dateObj = new Date(date + 'T12:00:00'); // noon to avoid date shift
  const dateOptions = style === 'long'
    ? { day: 'numeric', month: 'long', year: 'numeric' }
    : { day: '2-digit', month: '2-digit', year: 'numeric' };

  return dateObj.toLocaleDateString(undefined, dateOptions);
}

/**
 * Convert a date + time in a given timezone to a UTC Date object.
 * Pure JS approach without external libraries.
 */
function zonedTimeToUTC(dateStr, timeStr, timeZone) {
  // Create a date assuming it's UTC
  const naiveUTC = new Date(`${dateStr}T${timeStr}:00Z`);

  // Find out what Paris displays when it's that UTC time
  const utcInTz = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(naiveUTC);

  // Parse the formatted Paris time
  const [datePart, timePart] = utcInTz.split(', ');
  const [m, d, y] = datePart.split('/');
  const [h, min, s] = timePart.split(':');
  const parisAsDate = new Date(Date.UTC(+y, +m - 1, +d, +h === 24 ? 0 : +h, +min, +s));

  // The offset is the difference between parisAsDate and naiveUTC
  const offsetMs = parisAsDate - naiveUTC;

  // The actual UTC time = naiveUTC - offset
  return new Date(naiveUTC.getTime() - offsetMs);
}
